import {
  CardsThree,
  Chat,
  HandArrowUp,
  ThumbsUp,
  UserCircleMinus,
} from "@phosphor-icons/react";

import useTemplates from "features/templates/datahooks/useTemplates";

import RenderIf from "common/components/RenderIf";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import { REACTIONS } from "../../../../../constants";
import { useCampaignContext } from "../../../../../context/CampaignContext";
import ReviewHeader from "./ReviewHeader";
import ReviewItem from "./ReviewItem";

import type { CampaignFlow } from "../../../../../types";

interface FlowReviewProps {
  flow: CampaignFlow;
}

export default function FlowReview({ flow }: FlowReviewProps) {
  const {
    type,
    template,
    flow_message_templates: flowMessageTemplates,
    like_type: likeType,
    like_enabled: isLikeEnabled,
    endorse_enabled: isEndorseEnabled,
    withdraw_delay: withdrawDelay,
  } = flow;

  const { allTemplates, isLoadingAllTemplates } = useTemplates();
  const { accounts } = useSelectedWorkspaceContext();
  const { owners } = useCampaignContext();

  const workerAccounts = accounts.filter(({ id }) => owners.includes(id));
  const primaryTemplateLabel =
    type === "PRIMARY_CONNECT" ? "Connection Note" : "InMail Template";

  let primaryTemplateName = workerAccounts.every((account) => account.premium)
    ? "No template added"
    : "Not eligible";

  if (template) {
    const originalTemplate = allTemplates?.find(
      ({ id }) => id === template.original_id,
    );

    if (originalTemplate) {
      primaryTemplateName = originalTemplate.name;

      if (template.edited) {
        primaryTemplateName += " (Edited)";
      }
    } else {
      primaryTemplateName = `Deleted ${primaryTemplateLabel}`;
    }
  }

  const isAiEnabledInMessages = flowMessageTemplates.some(
    (template) =>
      template.type !== "VOICE_TEMPLATE" &&
      Object.keys(template.ai_descriptors).length,
  );
  const ReactionIcon = REACTIONS[likeType].icon;

  return (
    <section className="mb-4 overflow-hidden rounded-3xl border border-black-200 last:mb-0">
      {/* Header */}
      <ReviewHeader flow={flow} />

      <div className="flex flex-col p-3 md:p-4">
        {/* Template */}
        <RenderIf
          condition={
            type === "PRIMARY_CONNECT" || type === "INMAIL_OPTIMIZATION"
          }
        >
          <ReviewItem
            title={primaryTemplateLabel}
            value={primaryTemplateName}
            icon={CardsThree}
            isAiEnabled={
              template && !!Object.keys(template.ai_descriptors).length
            }
            isLoading={isLoadingAllTemplates}
          />
        </RenderIf>

        <RenderIf condition={isLikeEnabled}>
          <ReviewItem
            title="React to a post"
            value={
              <Tag variant="quaternary" leftIcon={<ReactionIcon />}>
                {REACTIONS[likeType].label}
              </Tag>
            }
            icon={ThumbsUp}
            isLoading={isLoadingAllTemplates}
          />
        </RenderIf>

        {/* Flow Template Messages */}
        <RenderIf condition={type !== "INMAIL_OPTIMIZATION"}>
          <ReviewItem
            value={
              flowMessageTemplates
                ? flowMessageTemplates.length
                : "No messaged added"
            }
            title="Messages in sequence"
            icon={Chat}
            isAiEnabled={isAiEnabledInMessages}
          />
        </RenderIf>

        <RenderIf condition={isEndorseEnabled}>
          <ReviewItem
            title="Endorse a skill"
            value="ON"
            icon={HandArrowUp}
            isLoading={isLoadingAllTemplates}
          />
        </RenderIf>

        <RenderIf condition={type === "PRIMARY_CONNECT"}>
          <ReviewItem
            title="Withdraw connection request"
            value={
              <Tag variant="quaternary">
                {withdrawDelay / (24 * 60 * 60 * 1000)} day
              </Tag>
            }
            icon={UserCircleMinus}
            isLoading={isLoadingAllTemplates}
          />
        </RenderIf>
      </div>
    </section>
  );
}
